import React, { useState, useMemo, useEffect  } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faArchive,
  faBoxOpen,
  faTrash,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { Alert, Badge } from 'react-bootstrap';
import UniversalReferenceModal from '../components/UniversalReferenceModal';
import { useReference } from '../actions/useReference';
import '../../QueueFull/cabinet-zdo-requests.css';
import './style.css';

const PositionsList = ({ isEditPossible = true }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [actionError, setActionError] = useState(null);

  const { 
    data, 
    config, 
    loading, 
    error,
    actions 
  } = useReference('positions');

  useEffect(()=>{
    actions.reload(activeTab == 'active'? 'active' : 'archived');
  },[activeTab])

  // Очистка ошибок при смене таба
  useEffect(() => {
    if (error) {
      actions.clearError();
    }
    setActionError(null);
  }, [activeTab]);

  // Маппинг типов для отображения
  const typeLabels = {
    administrative: { label: 'Адміністративний', variant: 'primary' },
    pedagogical: { label: 'Педагогічний', variant: 'success' },
    technical: { label: 'Технічний', variant: 'secondary' }
  };

  // Фильтрация данных по статусу
  const filteredData = useMemo(() => {
    return data.filter(item => item.status === activeTab);
  }, [data]);

  const columns = [
    { key: 'name', label: 'Найменування', sortable: true, width: '25%' },
    { key: 'type', label: 'Тип', sortable: true, width: '20%' },
    { key: 'createdAtFormatted', label: 'Дата створення', sortable: true, width: '15%' },
    { key: 'updatedAtFormatted', label: 'Дата редагування', sortable: true, width: '15%' },
    ...(isEditPossible ? [{ key: 'actions', label: 'Дії', sortable: false, width: '25%' }] : [])
  ];

  // Сортировка
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Для сортировки по типу используем русские названия
      if (sortConfig.key === 'type') {
        aValue = typeLabels[aValue]?.label || aValue;
        bValue = typeLabels[bValue]?.label || bValue;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, typeLabels]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, item) => {
    try {
      setActionError(null);
      
      if (actionId === 'edit') {
        setSelectedItem(item);
        setViewMode(false);
        setIsModalOpen(true);
      } else if (actionId === 'view') {
        setSelectedItem(item);
        setViewMode(true);
        setIsModalOpen(true);
      } else if (actionId === 'archive') {
        await actions.archive(item._id);
        await actions.reload(activeTab == 'active'? 'active' : 'archived');
      } else if (actionId === 'restore') {
        await actions.restore(item._id);
        await actions.reload(activeTab == 'active'? 'active' : 'archived');
      } else if (actionId === 'delete') {
        if (window.confirm(`Ви впевнені, що хочете назавжди видалити "${item.name}"?`)) {
          await actions.delete(item._id);
          await actions.reload(activeTab == 'active'? 'active' : 'archived');
        }
      }
    } catch (error) {
      console.error('Помилка при виконанні дії:', error);
      setActionError(error.message);
    } finally {
      setActiveMenuIndex(null);
    }
  };

  const handleAddItem = () => {
    if (!isEditPossible) return;
    setSelectedItem(null);
    setViewMode(false);
    setIsModalOpen(true);
    setActionError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setViewMode(false);
    setActionError(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setActionError(null);
      if (selectedItem) {
        await actions.update(selectedItem._id, formData);
      } else {
        await actions.create(formData);
      }
      await actions.reload(activeTab == 'active'? 'active' : 'archived');
      handleCloseModal();
    } catch (error) {
      console.error('Помилка при збереженні:', error);
      setActionError(error.message);
      handleCloseModal();
      
    }
  };

  const ActionsMenu = ({ onActionSelect, onClose, item, isArchived }) => {
    const baseActions = [
      { id: 'view', label: 'Переглянути', icon: faEye }
    ];

    const editActions = isArchived 
      ? [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'restore', label: 'Поновити', icon: faBoxOpen },
          { id: 'delete', label: 'Видалити', icon: faTrash }
        ]
      : [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'archive', label: 'В архів', icon: faArchive }
        ];

    const actions = isEditPossible 
      ? [...baseActions, ...editActions]
      : baseActions;

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, item)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon 
              icon={action.icon} 
              style={{ 
                width: '16px', 
                color: action.id === 'delete' ? '#dc3545' : '#6c757d' 
              }} 
            />
            <span style={{ 
              color: action.id === 'delete' ? '#dc3545' : '#212529' 
            }}>
              {action.label}
            </span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label">Довідник посад</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Актуальні
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archived')}
                    >
                      Архівні
                    </button>
                  </li>
                </ul>
              </div>
              {isEditPossible && (
                <div className="queuetable-switch-wrap">
                  <a 
                    href="" 
                    className="link-primary small text-nowrap" 
                    onClick={(e) => { e.preventDefault(); handleAddItem(); }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Додати нову посаду
                  </a>
                </div>
              )}
            </div>

            {/* Отображение ошибок */}
            {(error || actionError) && (
              <Alert 
                variant="danger" 
                className="mb-3"
                dismissible
                onClose={() => {
                  if (error) actions.clearError();
                  if (actionError) setActionError(null);
                }}
              >
                {error || actionError}
              </Alert>
            )}

            {/* Таблица */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table queuetable-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th 
                            key={column.key}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && handleSort(column.key)}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon 
                                  icon={getSortIcon(column.key)} 
                                  className="ms-1" 
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Завантаження...</span>
                            </div>
                            <p className="mt-2 mb-0">Завантаження посад...</p>
                          </td>
                        </tr>
                      ) : sortedData.length > 0 ? (
                        sortedData.map((item, index) => (
                          <tr key={item._id}>
                            <td>
                              <div>
                                <strong>{item.name}</strong>
                                {item.description && (
                                  <div className="small text-muted mt-1">
                                    {item.description}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge 
                                bg={typeLabels[item.additionalFields?.type]?.variant || 'secondary'}
                              >
                                {typeLabels[item.additionalFields?.type]?.label || item.type}
                              </Badge>
                            </td>
                            <td>{item.createdAtFormatted}</td>
                            <td>{item.updatedAtFormatted}</td>
                            {isEditPossible && (
                              <td className="queuetable-actions text-end">
                                <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    className="btn btn-sm btn-link text-decoration-none p-0"
                                    onClick={() => handleMenuToggle(index)}
                                    style={{ color: '#0d6efd' }}
                                    disabled={loading}
                                  >
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                  </button>
                                  
                                  {activeMenuIndex === index && (
                                    <ActionsMenu 
                                      onActionSelect={handleActionSelect}
                                      onClose={handleMenuClose}
                                      item={item}
                                      isArchived={activeTab === 'archived'}
                                    />
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            <div className="text-muted">
                              <p className="mb-1">Посади не знайдено</p>
                              <small>Спробуйте змінити вкладку або додати нову посаду</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Пагинация */}
                <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    Всього {sortedData.length} посад
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Универсальное модальное окно */}
      <UniversalReferenceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        referenceType="positions"
        config={config}
        itemData={selectedItem}
        onSubmit={handleSubmit}
        loading={loading}
        viewMode={viewMode}
        isEditPossible={isEditPossible}
        error={actionError}
        onClearError={() => setActionError(null)}
      />
    </>
  );
};

export default PositionsList;
